import { useCallback, useEffect, useState, useRef } from 'react';
import { showErrorMessage, showInfoMessage, showSuccessMessage } from '@/shared/components/toast-system';
import { useDispatch } from 'react-redux';
import type { Paddle, Environments, PaddleEventData } from '@paddle/paddle-js';
import { useAppSelector } from '@/shared/adapters/store';
import { updateUser } from '../../../redux/authSlice';
import {
  getSubscriptionPlans,
  getCurrentSubscription,
  checkSubscriptionTransaction,
  cancelSubscription,
  reactivateSubscription,
  createUpdatePaymentMethodTransaction,
  confirmPaymentMethodUpdate,
  revertSubscriptionChange,
} from '../../../utils/subscriptionApiUtils';
import Subscribe from '../components/Subscribe';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import StripeCheckoutModal from '../components/StripeCheckoutModal';
import PendingInvoiceDialog from '../components/PendingInvoiceDialog';
import PastDueBanner from '../components/PastDueBanner';
import SubscriptionStatusCard from '../components/SubscriptionStatusCard';
import { _t } from '@/i18n/t';
import { LogOut } from 'lucide-react';
import { setUpgradeInProgress } from '@/shared/utils/upgradeInProgress';
import { deriveSubscriptionState } from '../subscriptionStatus';
import {
  isPaddle,
  isStripe,
  isPaymentEnabled,
  getStripe,
  sleep,
  openCenteredPopup,
  closePopup,
  waitForPopupClose,
  startCheckoutOverlay,
  getResponseMessage,
  type PlanType,
  type SubscriptionInfoType,
  type OriginalStateType,
} from '../subscriptionShared';

const POLL_INTERVAL_MS = 1500;
const AUTH_TIMEOUT_MS = 5 * 60 * 1000;
const CLOSE_CONFIRM_ATTEMPTS = 3;
const CLOSE_CONFIRM_RETRY_MS = 1000;
const INVOICE_POLL_MS = 1000;

interface SubscriptionProps {
  isOverlay?: boolean;
}

interface SubscriptionAuthState {
  auth: {
    user?: {
      paymentCustomerId?: string | null;
      subscription?: SubscriptionInfoType;
      permissions?: Record<string, boolean | undefined>;
    } | null;
  };
}

type InvoiceOutcome = 'success' | 'failure' | 'timeout' | 'cancelled' | 'unknown';

const Subscription = ({ isOverlay = false }: SubscriptionProps) => {
  const dispatch = useDispatch();
  const [upgradePlans, setUpgradePlans] = useState<PlanType[]>([]);
  const [paddle, setPaddle] = useState<Paddle | null>(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [stripeCheckoutSecret, setStripeCheckoutSecret] = useState<string | null>(null);
  const [stripeCheckoutTransactionId, setStripeCheckoutTransactionId] = useState<string | null>(null);
  const [pendingInvoiceUrl, setPendingInvoiceUrl] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const isPaymentUpdateCheckoutRef = useRef(false);
  const paddleCheckoutEndRef = useRef<(() => void) | null>(null);

  const auth = useAppSelector((state: SubscriptionAuthState) => state.auth);
  const {
    paymentCustomerId,
    subscription: initialSubscriptionInfo,
    permissions,
  } = auth.user || {};

  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfoType | undefined>(
    initialSubscriptionInfo
  );

  const canManageSubscription = !!permissions?.ReadSubscription;

  const {
    planName,
    isCanceled,
    showPaymentBanner,
    showManageControls,
    statusLabel,
    statusBadgeClass,
    billingDetail,
  } = deriveSubscriptionState(subscriptionInfo);

  const fetchSubscription = async (fresh = false): Promise<SubscriptionInfoType> => {
    const response = await getCurrentSubscription(fresh);
    return response.data.data;
  };

  const refreshSubscriptionDetails = useCallback(async (fresh = false) => {
    try {
      const data = await fetchSubscription(fresh);
      setSubscriptionInfo(data);
      dispatch(updateUser({ subscription: data }));
      return data;
    } catch (error) {
      showErrorMessage(_t('settings.subscription.toast.refreshFailed'));
      console.error('Failed to refresh subscription details:', error);
      return null;
    }
  }, [dispatch]);

  const fetchUpgradePlans = useCallback(async () => {
    if (!paymentCustomerId) return;
    try {
      const response = await getSubscriptionPlans();
      setUpgradePlans(response.data.upgradePlans || []);
    } catch {
      showErrorMessage(_t('settings.subscription.toast.plansFailed'));
    }
  }, [paymentCustomerId]);

  const verifyTransaction = async (transactionId: string, pendingMessage: string) => {
    try {
      const response = await checkSubscriptionTransaction(transactionId);
      if (response.data.success) {
        showSuccessMessage(_t('settings.subscription.toast.activated'));
      } else {
        showInfoMessage(pendingMessage);
      }
    } catch {
      showErrorMessage(_t('settings.subscription.toast.verifyIssue'));
    }
  };

  useEffect(() => {
    if (!isPaddle) return;

    let cancelled = false;
    const paddleToken = import.meta.env.REACT_APP_PADDLE_TOKEN;
    const paddleEnv = import.meta.env.REACT_APP_ENV === 'production' ? 'live' : 'sandbox';

    import('@paddle/paddle-js')
      .then(({ initializePaddle }) =>
        initializePaddle({
          environment: paddleEnv as Environments,
          token: paddleToken,
          eventCallback: async function (data: PaddleEventData) {
            if (data.name !== 'checkout.closed') return;

            paddleCheckoutEndRef.current?.();
            paddleCheckoutEndRef.current = null;

            if (isPaymentUpdateCheckoutRef.current) {
              isPaymentUpdateCheckoutRef.current = false;
              return;
            }

            const transactionId = data.data?.transaction_id;
            if (!transactionId) return;

            try {
              await verifyTransaction(
                transactionId,
                _t('settings.subscription.toast.notActivatedIncomplete')
              );
            } finally {
              await refreshSubscriptionDetails();
            }
          },
        })
      )
      .then((paddleInstance) => {
        if (!cancelled && paddleInstance) setPaddle(paddleInstance);
      })
      .catch((err: unknown) => {
        console.error('Failed to initialise Paddle:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshSubscriptionDetails]);

  useEffect(() => {
    if (!isStripe) return;
    getStripe()?.catch((err: unknown) => {
      console.error('Failed to preload Stripe:', err);
    });
  }, []);

  useEffect(() => {
    if (canManageSubscription) {
      fetchUpgradePlans();
    }
  }, [fetchUpgradePlans, canManageSubscription]);

  useEffect(() => {
    setSubscriptionInfo(initialSubscriptionInfo);
  }, [initialSubscriptionInfo]);

  useEffect(() => {
    if (!stripeCheckoutSecret) return;
    return startCheckoutOverlay(_t('settings.subscription.overlay.stripeCheckout'));
  }, [stripeCheckoutSecret]);

  const openPaddleCheckout = (transactionId: string, isPaymentUpdate = false) => {
    isPaymentUpdateCheckoutRef.current = isPaymentUpdate;
    paddleCheckoutEndRef.current = startCheckoutOverlay(
      _t('settings.subscription.overlay.paddleCheckout')
    );
    try {
      paddle!.Checkout.open({ transactionId });
    } catch (err) {
      console.error('Failed to open Paddle checkout:', err);
      paddleCheckoutEndRef.current?.();
      paddleCheckoutEndRef.current = null;
      isPaymentUpdateCheckoutRef.current = false;
      showErrorMessage(_t('settings.subscription.toast.paddleCheckoutError'));
    }
  };

  const handleStripeCheckoutFinish = async () => {
    const txnId = stripeCheckoutTransactionId;
    const endOverlay = startCheckoutOverlay(_t('settings.subscription.overlay.confirming'));
    setStripeCheckoutSecret(null);
    setStripeCheckoutTransactionId(null);

    try {
      if (txnId) {
        await verifyTransaction(
          txnId,
          _t('settings.subscription.toast.notActivatedProcessing')
        );
      }
      await refreshSubscriptionDetails();
    } finally {
      endOverlay();
    }
  };

  const handleUpdatePaymentClick = async () => {
    setIsProcessingAction(true);

    const popup = isStripe ? openCenteredPopup('stripe-payment-update') : null;

    try {
      const response = await createUpdatePaymentMethodTransaction(subscriptionInfo!.id!);
      const result = response.data;

      if (isPaddle) {
        if (!result.success || !result.transactionId) {
          throw new Error('Could not retrieve payment update form.');
        }
        openPaddleCheckout(result.transactionId, true);
        return;
      }

      if (!isStripe) return;

      if (!result.success || !result.portalUrl) {
        throw new Error('Could not retrieve payment update form.');
      }

      if (!popup || popup.closed) {
        window.location.href = result.portalUrl;
        return;
      }

      popup.location.href = result.portalUrl;
      const endOverlay = startCheckoutOverlay(
        _t('settings.subscription.overlay.paymentWindow')
      );

      waitForPopupClose(popup).then(async () => {
        endOverlay();
        try {
          const confirmResponse = await confirmPaymentMethodUpdate(subscriptionInfo!.id!);
          const confirmResult = confirmResponse.data;

          if (confirmResult.paymentRequired && confirmResult.hostedUrl) {
            setPendingInvoiceUrl(confirmResult.hostedUrl);
            showSuccessMessage(
              _t('settings.subscription.toast.paymentMethodUpdatedPayRequired')
            );
          } else {
            showSuccessMessage(_t('settings.subscription.toast.paymentMethodUpdated'));
          }
          await refreshSubscriptionDetails(true);
        } catch {
          showInfoMessage(_t('settings.subscription.toast.paymentMethodUpdateUnknown'));
          await refreshSubscriptionDetails();
        }
      });
    } catch (err) {
      closePopup(popup);
      console.error('Failed to open update payment method:', err);
      showErrorMessage(_t('settings.subscription.toast.paymentUpdateFormError'));
    } finally {
      setIsProcessingAction(false);
    }
  };

  // Shared invoice-payment popup flow. Opens the hosted Stripe invoice page in a
  // centred popup (synchronously, to survive the popup blocker), then refreshes
  // once the user closes it. Used both after a card update (PendingInvoiceDialog)
  // and when retrying payment directly from the past-due banner.
  const openInvoicePaymentPopup = (invoiceUrl: string) => {
    const invoicePopup = openCenteredPopup('stripe-invoice-payment', invoiceUrl);

    if (!invoicePopup || invoicePopup.closed) {
      window.location.href = invoiceUrl;
      return;
    }

    const endOverlay = startCheckoutOverlay(
      _t('settings.subscription.overlay.paymentWindow')
    );

    waitForPopupClose(invoicePopup, INVOICE_POLL_MS).then(async () => {
      endOverlay();
      const fresh = await refreshSubscriptionDetails(true);
      if (fresh && fresh.status !== 'past_due' && !fresh.hasUnpaidInvoice) {
        showSuccessMessage(_t('settings.subscription.toast.paymentCompleted'));
      } else {
        showInfoMessage(_t('settings.subscription.toast.paymentMayNotHaveCompleted'));
      }
    });
  };

  const handleCompletePaymentClick = () => {
    if (!pendingInvoiceUrl) return;
    const invoiceUrl = pendingInvoiceUrl;
    setPendingInvoiceUrl(null);
    openInvoicePaymentPopup(invoiceUrl);
  };

  // Retry the outstanding invoice without changing the card. The URL is only
  // present on past-due subscriptions with an open invoice, so the banner button
  // that calls this is likewise only ever shown in that state.
  const handleRetryPaymentClick = () => {
    const invoiceUrl = subscriptionInfo?.hostedInvoiceUrl;
    if (!invoiceUrl) return;
    openInvoicePaymentPopup(invoiceUrl);
  };

  const revertChange = async (
    subscriptionId: string,
    originalState: OriginalStateType | null
  ) => {
    if (!subscriptionId || !originalState) return;
    try {
      await revertSubscriptionChange(subscriptionId, originalState);
    } catch (e) {
      console.error('Revert subscription change failed:', e);
    }
  };

  const resolveAfterPopupClose = async (): Promise<InvoiceOutcome> => {
    for (let attempt = 1; attempt <= CLOSE_CONFIRM_ATTEMPTS; attempt += 1) {
      try {
        const fresh = await fetchSubscription(true);
        const settled = !fresh.hasUnpaidInvoice && fresh.status !== 'canceled';
        return settled ? 'success' : 'cancelled';
      } catch (e) {
        console.warn(
          `Could not read subscription after popup close (attempt ${attempt}):`,
          e
        );
        if (attempt < CLOSE_CONFIRM_ATTEMPTS) await sleep(CLOSE_CONFIRM_RETRY_MS);
      }
    }
    return 'unknown';
  };

  const handleHostedInvoicePopup = async (
    popup: Window,
    hostedUrl: string,
    subscriptionId: string,
    originalState: OriginalStateType | null
  ): Promise<void> => {
    setUpgradeInProgress(true);
    try {
      try {
        popup.location.href = hostedUrl;
      } catch (e) {
        console.error('Failed to navigate popup to hosted invoice URL:', e);
        closePopup(popup);
        showErrorMessage(_t('settings.subscription.toast.couldNotOpenAuth'));
        await revertChange(subscriptionId, originalState);
        return;
      }

      const deadline = Date.now() + AUTH_TIMEOUT_MS;
      let outcome: InvoiceOutcome | null = null;

      while (outcome === null) {
        if (popup.closed) {
          outcome = await resolveAfterPopupClose();
          break;
        }

        try {
          const fresh = await fetchSubscription(true);
          if (fresh.hasUnpaidInvoice === false && fresh.status !== 'canceled') {
            outcome = 'success';
          } else if (
            fresh.status === 'incomplete_expired' ||
            fresh.status === 'canceled'
          ) {
            outcome = 'failure';
          }
        } catch (e) {
          console.warn('Poll error during 3DS confirmation:', e);
        }

        if (outcome !== null) break;
        if (Date.now() >= deadline) {
          outcome = 'timeout';
          break;
        }
        await sleep(POLL_INTERVAL_MS);
      }

      closePopup(popup);

      switch (outcome) {
        case 'success':
          showSuccessMessage(_t('settings.subscription.toast.changed'));
          break;
        case 'failure':
          showErrorMessage(_t('settings.subscription.toast.authFailed'));
          await revertChange(subscriptionId, originalState);
          break;
        case 'timeout':
          showErrorMessage(_t('settings.subscription.toast.authTimedOut'));
          await revertChange(subscriptionId, originalState);
          break;
        case 'cancelled':
          showInfoMessage(_t('settings.subscription.toast.paymentCancelled'));
          await revertChange(subscriptionId, originalState);
          break;
        case 'unknown':
          showInfoMessage(_t('settings.subscription.toast.statusUnconfirmed'));
          break;
      }

      await refreshSubscriptionDetails();
    } finally {
      setUpgradeInProgress(false);
    }
  };

  const executeCancel = async () => {
    setIsProcessingAction(true);
    try {
      await cancelSubscription(subscriptionInfo!.id!);
      showSuccessMessage(_t('settings.subscription.toast.canceled'));
      await refreshSubscriptionDetails();
    } catch (err) {
      showErrorMessage(
        getResponseMessage(err) || _t('settings.subscription.toast.cancelFailed')
      );
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleReactivateClick = async () => {
    setIsProcessingAction(true);
    try {
      await reactivateSubscription(subscriptionInfo!.id!);
      showSuccessMessage(_t('settings.subscription.toast.reactivated'));
      await refreshSubscriptionDetails();
    } catch (err) {
      showErrorMessage(
        getResponseMessage(err) || _t('settings.subscription.toast.reactivateFailed')
      );
    } finally {
      setIsProcessingAction(false);
    }
  };

  if (!isPaymentEnabled) {
    const disabledRootClass = isOverlay
      ? 'w-full flex flex-col'
      : 'w-full flex flex-col h-full flex-1';

    return (
      <div className={disabledRootClass}>
        <div className="p-8 max-w-4xl mx-auto w-full text-muted-foreground">
          {_t('settings.subscription.disabled')}
        </div>
      </div>
    );
  }

  const isCheckoutReady = isPaddle ? !!paddle : isStripe;

  const rootClass = isOverlay
    ? 'w-full flex flex-col bg-transparent'
    : 'w-full flex flex-col bg-transparent h-full flex-1';

  const contentClass = isOverlay
    ? 'px-8 pt-8 pb-10 w-full relative flex flex-col'
    : 'px-8 pt-8 pb-10 w-full relative flex flex-col max-w-4xl mx-auto flex-1';

  return (
    <div className={rootClass}>
      <ConfirmDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onConfirm={executeCancel}
        title={_t('settings.subscription.cancelDialog.title')}
        description={_t('settings.subscription.cancelDialog.description')}
        confirmLabel={_t('settings.subscription.cancelDialog.confirm')}
        confirmVariant="danger"
        cancelLabel={_t('kb.actions.cancel')}
        elevated={isOverlay}
      />

      {isStripe && stripeCheckoutSecret && (
        <StripeCheckoutModal
          clientSecret={stripeCheckoutSecret}
          onFinish={handleStripeCheckoutFinish}
        />
      )}

      {pendingInvoiceUrl && (
        <PendingInvoiceDialog
          onClose={() => setPendingInvoiceUrl(null)}
          onPayNow={handleCompletePaymentClick}
        />
      )}

      <div className={contentClass}>
        {isOverlay && (
          
          <a  href="/logout"
            className="absolute top-5 right-5 flex items-center gap-2 px-3 py-2 text-sm text-foreground no-underline z-10 bg-muted rounded-md hover:bg-slate-200 transition-colors"
          >
            <LogOut className="size-4" />
            {_t('settings.subscription.logout')}
          </a>
        )}

        {}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-foreground tracking-tight leading-8">
            {isOverlay
              ? _t('settings.subscription.pleaseSubscribe')
              : _t('settings.subscription.yourSubscription')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isOverlay
              ? _t('settings.subscription.choosePlanToContinue')
              : _t('settings.subscription.managePlanAndBilling')}
          </p>
        </div>

        {showPaymentBanner && (
          <PastDueBanner
            isProcessing={isProcessingAction}
            canRetryPayment={!!subscriptionInfo?.hostedInvoiceUrl}
            onRetryPayment={handleRetryPaymentClick}
            onUpdatePayment={handleUpdatePaymentClick}
          />
        )}

        {subscriptionInfo && subscriptionInfo.status !== 'canceled' && (
          <SubscriptionStatusCard
            planName={planName}
            statusLabel={statusLabel}
            statusBadgeClass={statusBadgeClass}
            billingDetail={billingDetail}
            showManageControls={showManageControls}
            isCanceled={isCanceled}
            showPaymentBanner={showPaymentBanner}
            isProcessing={isProcessingAction}
            onUpdatePayment={handleUpdatePaymentClick}
            onCancel={() => setShowCancelDialog(true)}
            onReactivate={handleReactivateClick}
          />
        )}

        <div className="flex flex-col flex-1">
          {canManageSubscription ? (
            <Subscribe
              plans={upgradePlans}
              subscriptionInfo={subscriptionInfo}
              isCheckoutReady={isCheckoutReady}
              isOverlay={isOverlay}
              onPaddleCheckout={openPaddleCheckout}
              onStripeCheckout={(clientSecret: string, transactionId: string) => {
                setStripeCheckoutSecret(clientSecret);
                setStripeCheckoutTransactionId(transactionId);
              }}
              onHostedInvoiceRequired={handleHostedInvoicePopup}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center w-full">
              <p className="text-center text-muted-foreground">
                {_t('settings.subscription.contactAdmin')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subscription;